import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useCompany, DAY_KEYS } from "@/lib/useCompany";
import { useI18n } from "@/i18n/context";

const DAY_TO_SCHEMA = { mo: "Mo", tu: "Tu", we: "We", th: "Th", fr: "Fr", sa: "Sa", su: "Su" };

/**
 * StructuredData — inserts JSON-LD LocalBusiness metadata AND sets per-path
 * SEO title/description/og:image based on the DB seo_metadata collection.
 * Falls back to company defaults when no per-path entry exists.
 */
export default function StructuredData() {
  const c = useCompany();
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const [seoEntries, setSeoEntries] = useState([]);

  useEffect(() => {
    const load = () => api.get("/seo-metadata").then(r => setSeoEntries(r.data)).catch(() => setSeoEntries([]));
    load();
    const onUpd = () => load();
    window.addEventListener("handymeister:data-updated", onUpd);
    return () => window.removeEventListener("handymeister:data-updated", onUpd);
  }, []);

  useEffect(() => {
    if (!c?.company_name) return;
    // Structured data (LocalBusiness)
    const spec = DAY_KEYS
      .filter((k) => c.opening_hours?.[k] && !c.opening_hours[k].closed)
      .map((k) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: DAY_TO_SCHEMA[k],
        opens: c.opening_hours[k].open_time,
        closes: c.opening_hours[k].close_time,
      }));
  const data = {
      "@context": "https://schema.org", "@type": ["LocalBusiness", "MobilePhoneStore"],
      name: c.company_name, legalName: c.legal_name,
      url: window.location.origin,
      image: c.logo_url || undefined,
      telephone: c.phone, email: c.email,
      priceRange: "€€",
      sameAs: Object.values(c.social_links || {}).filter(Boolean),
      address: {
        "@type": "PostalAddress",
        streetAddress: c.street, postalCode: c.postal_code,
        addressLocality: c.city, addressCountry: c.country,
      },
      openingHoursSpecification: spec,
    };
    let ld = document.getElementById("structured-data-localbusiness");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "structured-data-localbusiness";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(data);

    // Per-path SEO overrides
    const entry = seoEntries.find((e) => e.path === pathname);
    const canonicalUrl = `${window.location.origin}${pathname}`;
    const title = lang === "en"
      ? (entry?.title_en || entry?.title || `${c.company_name} - Smartphone Repair ${c.city}`)
      : lang === "ar"
        ? (entry?.title_ar || `${c.company_name} - إصلاح الهواتف الذكية`)
        : (entry?.title || `${c.company_name} - Smartphone-Reparatur ${c.city}`);
    const description = lang === "en"
      ? (entry?.description_en || entry?.description || `${c.company_name} - Professional smartphone repair in ${c.city}.`)
      : lang === "ar"
        ? (entry?.description_ar || `${c.company_name} - إصلاح احترافي للهواتف الذكية مع أسعار واضحة وضمان.`)
        : (entry?.description || `${c.company_name} - Professionelle Smartphone-Reparatur in ${c.city}. Kontakt: ${c.phone} · ${c.email}`);
    const ogImage = entry?.og_image || c.logo_url || "";

    document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);

    // Open Graph
    for (const [prop, val] of [
      ["og:title", title], ["og:description", description],
      ["og:type", "website"], ["og:image", ogImage], ["og:url", canonicalUrl],
      ["og:locale", lang === "en" ? "en_GB" : lang === "ar" ? "ar_AR" : "de_DE"],
    ]) {
      if (!val) continue;
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    }

    for (const [name, value] of [
      ["twitter:card", "summary_large_image"], ["twitter:title", title],
      ["twitter:description", description], ["twitter:image", ogImage],
    ]) {
      if (!value) continue;
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", value);
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);
  }, [c, pathname, seoEntries, lang]);

  return null;
}
